import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type SubmissionStatus = 'Pending' | 'Verified' | 'Rejected';

type ReviewItem = {
  id: number;
  title: string;
  content: string;
  media_type: 'text' | 'video' | 'image';
  media_url: string;
  location: string;
  category_id: number;
  category_name: string;
  tags: string[];
  author_id: number;
  author_name: string;
  status: SubmissionStatus;
  createdAt: string;
};

const STORAGE_KEY = 'citizen_submission_posts';

export default function ViewContent() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const parsed: ReviewItem[] = saved ? JSON.parse(saved) : [];
      setItems(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
      console.error('Failed to load submitted posts:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const stats = useMemo(() => {
    return {
      total: items.length,
      pending: items.filter((item) => item.status === 'Pending').length,
      verified: items.filter((item) => item.status === 'Verified').length,
      rejected: items.filter((item) => item.status === 'Rejected').length,
    };
  }, [items]);

  const updateStatus = (id: number, status: SubmissionStatus) => {
    const updated = items.map((item) =>
      item.id === id ? { ...item, status } : item
    );

    setItems(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate('/citizen-submission')}
              className="text-red-700 hover:text-red-800 font-medium mb-3 inline-flex items-center gap-2"
            >
              <i className="ri-arrow-left-line"></i>
              Back to submission
            </button>
            <h1 className="text-4xl font-bold text-gray-900">Content Review</h1>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Verified</p>
            <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Rejected</p>
            <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-600">
            Loading submissions...
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-600">
            No submissions available for review.
          </div>
        ) : (
          <div className="space-y-6">
            {items.map((item) => (
              <article key={item.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                    <div>
                      <p className="text-sm text-red-700 font-medium uppercase tracking-wide">{item.category_name}</p>
                      <h2 className="text-2xl font-bold text-gray-900 mt-1">{item.title}</h2>
                    </div>

                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        item.status === 'Verified'
                          ? 'bg-green-100 text-green-800'
                          : item.status === 'Rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>

                  <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
                    <div>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{item.content}</p>

                      <div className="mt-6 flex flex-wrap gap-2">
                        {item.tags.map((tag) => (
                          <span key={tag} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                        {item.media_type === 'video' ? (
                          <video src={item.media_url} controls className="w-full rounded-lg max-h-64" />
                        ) : (
                          <img src={item.media_url} alt={item.title} className="w-full rounded-lg object-cover max-h-64" />
                        )}
                      </div>

                      <div className="text-sm text-gray-600 space-y-1">
                        <p><span className="font-medium text-gray-700">Author:</span> {item.author_name}</p>
                        <p><span className="font-medium text-gray-700">Location:</span> {item.location}</p>
                        <p><span className="font-medium text-gray-700">Submitted:</span> {new Date(item.createdAt).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => updateStatus(item.id, 'Verified')}
                      className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 transition-colors duration-300 font-medium"
                    >
                      Verify
                    </button>
                    <button
                      type="button"
                      onClick={() => updateStatus(item.id, 'Rejected')}
                      className="bg-red-600 text-white px-5 py-2.5 rounded-lg hover:bg-red-700 transition-colors duration-300 font-medium"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
